// Copyright (c) Docugami, Inc. All rights reserved.

import { Icon, IconButton, Stack, TextField, TooltipDelay, TooltipHost } from "@fluentui/react";
import { dgFontFamily } from "components/common/Fonts";
import { emptyColor, homeGray, primaryTheme, secondaryTheme } from "components/shell/presentation/AppStyles";
import { useKeyPressHandler } from "hooks/KeyPressHandler";
import { ReactNode, useState } from "react";

interface FilterCommandBarProps {
    onDismiss: () => void;
    searchProps?: SearchTextFieldProps;
    children?: ReactNode;
    hidden?: boolean;
}

export const ExplorerFilterCommandBar = (props: FilterCommandBarProps): JSX.Element | null => {
    if (!!props.hidden) {
        return null;
    }

    return (
        <Stack grow={1} hidden={props.hidden} horizontal tokens={{ padding: '10px', childrenGap: '10px' }} styles={{ root: { backgroundColor: emptyColor, borderRadius: '3px' } }}>
            {
                !!props.searchProps && (
                    <Stack.Item grow={1}>
                        <SearchTextField {...props.searchProps} />
                    </Stack.Item>
                )
            }
            <Stack horizontal tokens={{ childrenGap: '10px' }} styles={{ root: { marginLeft: "auto" } }}>
                {props.children}
                <TooltipHost content="Clear filters" calloutProps={{ isBeakVisible: false }} delay={TooltipDelay.zero}>
                    <IconButton styles={{ root: { color: homeGray }, icon: { fontSize: '14px' } }} iconProps={{ iconName: 'ChromeClose' }} onClick={props.onDismiss} />
                </TooltipHost>
            </Stack>
        </Stack>
    )
}

interface SearchTextFieldProps {
    searchPlaceholder?: string;
    onSearch: (searchString: string) => void;
}

const SearchTextField = (props: SearchTextFieldProps): JSX.Element => {
    const [value, setValue] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useKeyPressHandler('Enter', onSearch);

    function onSearch() {
        const trimmedValue = value.trim();
        setSearchValue(trimmedValue);
        props.onSearch(trimmedValue);
        setIsSubmitting(false);
    }

    function onBlur() {
        // Reset state to the previously searched value when we focus out
        if (!isSubmitting) {
            setValue(searchValue);
        }
    }

    function onRenderInput<T>(inputProps?: T, defaultRender?: (props?: T) => JSX.Element | null) {
        const searchDisabled = value.trim() === searchValue;
        return (
            <Stack grow={1} horizontal verticalAlign="center" tokens={{ childrenGap: '5px', padding: '0 0 0 5px' }}>
                <Stack.Item align="center" style={{ display: 'flex' }}>
                    <Icon iconName="Filter" style={{ color: homeGray }} />
                </Stack.Item>
                {defaultRender?.(inputProps)}
                <Stack.Item>
                    <IconButton
                        title="Search"
                        theme={secondaryTheme}
                        iconProps={{ iconName: 'Search' }}
                        disabled={searchDisabled}
                        onClick={onSearch}
                        onMouseDown={() => setIsSubmitting(true)}
                        styles={{
                            root: {
                                color: !searchDisabled ? primaryTheme.palette.white : homeGray,
                                backgroundColor: !searchDisabled ? primaryTheme.palette.themePrimary : 'inherit',
                                ".ms-Button:hover": {
                                    backgroundColor: primaryTheme.palette.themeDarkAlt
                                },
                                ".ms-Button:active": {
                                    backgroundColor: primaryTheme.palette.themeDark
                                }
                            }
                        }} />
                </Stack.Item>
            </Stack>
        )
    }

    return <TextField
        value={value}
        onBlur={onBlur}
        onChange={(ev, val) => setValue(val ?? '')}
        placeholder={props.searchPlaceholder ?? "Filter"}
        styles={{ root: { fontFamily: dgFontFamily }, fieldGroup: { background: 'inherit', border: 'none' } }}
        onRenderInput={onRenderInput} />
}